import type { ContinueClickedProperties } from '@blinq/analytics/browser';
import type { PaidPlan } from '@hooks/billing-subscription';
import { breakpoints } from '@ui-lib/css';

import { AMPLI_PLATFORM, trackAmpli } from './ampli';

/**
 * Analytics helpers for the Cello referral program's referee web flow
 * (landing page → signup → plan selection → Stripe). Event and property
 * shapes follow the ACT-2014 event design via the Ampli tracking plan.
 */
export const REFERRAL_LANDING_REF_PAGE = 'referral_program_landing';
export const PLAN_SELECT_REF_PAGE = 'plan_select';

export type ReferralSurface = 'mobile' | 'desktop';
export type PlanSelectOpenSource = 'referral_landing' | 'web_onboarding';
export type SelectedPlan = PaidPlan | 'free';
export type SelectedTerm = 'monthly' | 'annual';

export function getReferralSurface(isMobile: boolean): ReferralSurface {
  return isMobile ? 'mobile' : 'desktop';
}

/** Client-only: reads the viewport at call time, for components without a media hook. */
export function detectReferralSurface(): ReferralSurface {
  const isMobile =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(`(max-width: ${breakpoints.tablet - 1}px)`).matches;

  return getReferralSurface(isMobile);
}

export function trackReferralLandingViewed({
  surface,
  referralCode,
}: {
  surface: ReferralSurface;
  referralCode: string | null;
}): void {
  trackAmpli((ampli) =>
    ampli.referralProgramLandingViewed({
      ref_page: REFERRAL_LANDING_REF_PAGE,
      surface,
      referral_code: referralCode ?? undefined,
    }),
  );
}

export function trackReferralLandingContinueClicked(surface: ReferralSurface): void {
  // `continue_clicked` is a shared legacy event whose tracking plan marks several
  // onboarding-only properties (colour_changed, has_company_name, ...) as required.
  // The landing CTA has no meaningful values for those, so the payload is cast until
  // the data team relaxes them to optional in the plan.
  trackAmpli((ampli) =>
    ampli.continueClicked({
      ref_page: REFERRAL_LANDING_REF_PAGE,
      surface,
      platform: AMPLI_PLATFORM,
    } as ContinueClickedProperties),
  );
}

export function trackPlanSelectViewed({
  surface,
  openSource,
  discountPercentage,
}: {
  surface: ReferralSurface;
  openSource: PlanSelectOpenSource;
  discountPercentage: number;
}): void {
  trackAmpli((ampli) =>
    ampli.planSelectViewed({
      ref_page: PLAN_SELECT_REF_PAGE,
      surface,
      open_source: openSource,
      discount_percentage: discountPercentage,
    }),
  );
}

export function trackPlanSelected({
  surface,
  plan,
  term,
  discountPercentage,
}: {
  surface: ReferralSurface;
  plan: SelectedPlan;
  term: SelectedTerm;
  discountPercentage: number;
}): void {
  trackAmpli((ampli) =>
    ampli.planSelected({
      ref_page: PLAN_SELECT_REF_PAGE,
      surface,
      plan,
      term,
      discount_percentage: discountPercentage,
    }),
  );
}
